import { useEffect, useRef } from 'react';
import type { ComponentType, SVGProps } from 'react';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Warehouse,
  Tag,
  Star,
  Mail,
  Settings,
  ChevronLeft,
  PanelLeftClose,
  PanelLeftOpen,
  FolderTree,
  RotateCcw,
  FileText,
  MessageCircleQuestion,
  MessageSquareQuote,
  Activity,
  Award,
  X,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Logo } from '@/components/branding/Logo';
import { useMediaQuery } from '@/hooks/useClient';

export type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  badge?: string | number;
  group?: string;
};

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: "Vue d'ensemble", icon: LayoutDashboard, group: 'overview' },

  // Ventes
  { to: '/orders', label: 'Commandes', icon: ShoppingCart, group: 'sales' },
  { to: '/draft-orders', label: 'Brouillons', icon: FileText, group: 'sales' },
  { to: '/returns', label: 'Retours', icon: RotateCcw, group: 'sales' },

  // Catalogue
  { to: '/products', label: 'Produits', icon: Package, group: 'catalog' },
  { to: '/categories', label: 'Catégories', icon: FolderTree, group: 'catalog' },
  { to: '/inventory', label: 'Inventaire', icon: Warehouse, group: 'catalog' },

  // Clients
  { to: '/customers', label: 'Clients', icon: Users, group: 'customers' },

  // Marketing
  { to: '/promotions', label: 'Promotions', icon: Tag, group: 'marketing' },
  { to: '/referrals', label: 'Parrainage & Club', icon: Award, group: 'marketing' },
  { to: '/reviews', label: 'Avis clients', icon: Star, group: 'marketing' },
  { to: '/newsletter', label: 'Newsletter', icon: Mail, group: 'marketing' },

  // Contenus CMS
  { to: '/journal', label: 'Journal (Blog)', icon: FileText, group: 'cms' },
  { to: '/policies', label: 'Politiques & CGV', icon: RotateCcw, group: 'cms' },
  { to: '/site-config', label: 'Bannières & Textes', icon: Settings, group: 'cms' },
  { to: '/faq', label: 'FAQ', icon: MessageCircleQuestion, group: 'cms' },
  { to: '/testimonials', label: 'Témoignages', icon: MessageSquareQuote, group: 'cms' },

  // Configuration
  { to: '/settings', label: 'Paramètres', icon: Settings, group: 'system' },
  { to: '/system/dlq', label: 'File DLQ & Tâches', icon: Activity, group: 'system' },
];

const GROUP_LABELS: Record<string, string> = {
  overview: 'Aperçu',
  sales: 'Ventes',
  catalog: 'Catalogue',
  customers: 'Clients',
  marketing: 'Marketing',
  cms: 'Contenus CMS',
  system: 'Configuration',
};

/**
 * Pre-computed at module load. `NAV_ITEMS` is constant, so this avoids
 * re-running the reduce on every Sidebar render.
 */
const GROUPED_NAV_ITEMS: Record<string, NavItem[]> = NAV_ITEMS.reduce<Record<string, NavItem[]>>(
  (acc, item) => {
    const key = item.group ?? 'main';
    acc[key] = acc[key] ?? [];
    acc[key].push(item);
    return acc;
  },
  {}
);

type SidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) => {
  const location = useLocation();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const asideRef = useRef<HTMLElement>(null);
  const isMobileDialog = !isDesktop && mobileOpen;
  const isCollapsed = isDesktop && collapsed;

  // Mobile drawer focus trap + initial focus + restore on close.
  useEffect(() => {
    if (!isMobileDialog) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const aside = asideRef.current;
    if (!aside) return;

    const focusFirst = (): void => {
      const focusables = aside.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusables[0];
      if (first) {
        first.focus();
      } else {
        aside.setAttribute('tabindex', '-1');
        aside.focus();
      }
    };
    const id = window.setTimeout(focusFirst, 0);

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(aside.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      // `noUncheckedIndexedAccess` widens array element access to
      // `T | undefined`. The early-return above guarantees a non-empty
      // array, so the non-null assertions are safe.
      const first = focusables[0] as HTMLElement;
      const last = focusables[focusables.length - 1] as HTMLElement;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !aside.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !aside.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(id);
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isMobileDialog]);

  return (
    <>
      {/* Mobile overlay */}
      {!isDesktop && mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-900/40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        ref={asideRef}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border-base bg-surface-base shadow-2xl transition-all duration-200 ease-luxury lg:sticky lg:top-0 lg:h-screen lg:shadow-none',
          isCollapsed ? 'w-16' : 'w-72 max-w-[85vw] lg:w-60',
          !isDesktop && (mobileOpen ? 'translate-x-0' : '-translate-x-full')
        )}
        aria-label="Navigation principale"
        {...(isMobileDialog
          ? {
              role: 'dialog',
              'aria-modal': 'true' as const,
              'aria-labelledby': 'sidebar-nav-title',
            }
          : {})}
      >
        <h2 id="sidebar-nav-title" className="sr-only">
          Navigation principale
        </h2>

        {/* Logo & Mobile Close */}
        <div
          className={cn(
            'flex h-14 items-center justify-between border-b border-border-base px-4',
            isCollapsed && 'justify-center px-2'
          )}
        >
          <Link to="/" className="flex items-center gap-2" onClick={onMobileClose}>
            <Logo size={28} />
            {!isCollapsed && (
              <div className="flex flex-col leading-tight">
                <span className="font-serif text-base font-semibold text-fg-base">Sweet Home</span>
                <span className="text-2xs uppercase tracking-wider text-fg-muted">Admin</span>
              </div>
            )}
          </Link>
          {!isDesktop && (
            <button
              type="button"
              onClick={onMobileClose}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-surface-muted hover:text-fg-base active:bg-surface-subtle"
              aria-label="Fermer la navigation"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {Object.entries(GROUPED_NAV_ITEMS).map(([group, items]) => (
            <div key={group} className="mb-4">
              {!isCollapsed && GROUP_LABELS[group] && (
                <p className="mb-1 px-3 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
                  {GROUP_LABELS[group]}
                </p>
              )}
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    item.to === '/'
                      ? location.pathname === '/'
                      : location.pathname === item.to ||
                        location.pathname.startsWith(`${item.to}/`);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        onClick={onMobileClose}
                        title={isCollapsed ? item.label : undefined}
                        aria-label={isCollapsed ? item.label : undefined}
                        aria-current={isActive ? 'page' : undefined}
                        className={cn(
                          'group flex min-h-[44px] items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-burgundy-50 text-burgundy-700'
                            : 'text-fg-subtle hover:bg-surface-muted hover:text-fg-base active:bg-surface-subtle',
                          isCollapsed && 'min-h-[40px] justify-center px-2'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-4 w-4 flex-shrink-0',
                            isActive
                              ? 'text-burgundy-700'
                              : 'text-fg-muted group-hover:text-fg-base'
                          )}
                        />
                        {!isCollapsed && <span className="flex-1 truncate">{item.label}</span>}
                        {!isCollapsed && item.badge !== undefined && (
                          <span className="rounded-full bg-burgundy-600 px-1.5 text-2xs font-semibold text-fg-inverse">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse toggle (desktop) */}
        <div className="hidden border-t border-border-base p-2 lg:block">
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-fg-subtle transition-colors hover:bg-surface-muted hover:text-fg-base"
            aria-label={collapsed ? 'Étendre la barre latérale' : 'Réduire la barre latérale'}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4" />
                <span>Réduire</span>
              </>
            )}
          </button>
        </div>

        {/* Mobile close */}
        {!isDesktop && (
          <div className="border-t border-border-base p-2 lg:hidden">
            <button
              type="button"
              onClick={onMobileClose}
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm text-fg-subtle hover:bg-surface-muted active:bg-surface-subtle"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Fermer le menu</span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
};
